import { useNavigate } from 'react-router-dom';
import { Users, Bot, Server, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import StatusBadge, { deriveStatus } from './StatusBadge';
import type { TeamOverview } from '@/lib/types';

interface TeamCardProps {
  team: TeamOverview;
  index: number;
}

export default function TeamCard({ team, index }: TeamCardProps) {
  const navigate = useNavigate();
  const status = deriveStatus(
    team.services.healthy,
    team.services.unhealthy,
    team.services.total,
  );

  const healthPercent = team.services.total > 0
    ? Math.round((team.services.healthy / team.services.total) * 100)
    : 0;

  return (
    <button
      onClick={() => navigate(`/teams/${team.team_id}`)}
      className={cn(
        'group relative text-left w-full rounded-2xl p-5 transition-all duration-300',
        'bg-white/[0.02] border border-white/[0.06]',
        'hover:bg-white/[0.04] hover:border-white/[0.12] hover:shadow-card-hover',
        'hover:-translate-y-0.5',
        'focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-0',
      )}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Subtle top accent line reflecting health status */}
      <div
        className={cn(
          'absolute top-0 left-4 right-4 h-px',
          status === 'healthy' && 'bg-gradient-to-r from-transparent via-status-healthy/40 to-transparent',
          status === 'critical' && 'bg-gradient-to-r from-transparent via-status-critical/40 to-transparent',
          status === 'warning' && 'bg-gradient-to-r from-transparent via-status-warning/40 to-transparent',
          status === 'unknown' && 'bg-gradient-to-r from-transparent via-surface-600/40 to-transparent',
        )}
      />

      {/* Header: team name + status */}
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-white text-lg truncate group-hover:text-primary-300 transition-colors">
            {team.team_id}
          </h3>
          <p className="text-surface-500 text-xs font-mono mt-0.5 truncate">
            {team.domain || `${team.team_id}.kaiandco.ai`}
          </p>
        </div>
        <div className="flex-shrink-0 ml-3">
          <StatusBadge status={status} size="sm" pulse={status === 'critical'} />
        </div>
      </div>

      {/* Health bar */}
      {team.services.total > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-surface-400">Service health</span>
            <span className={cn(
              'text-xs font-mono font-medium',
              status === 'healthy' ? 'text-status-healthy' : status === 'critical' ? 'text-status-critical' : 'text-surface-400',
            )}>
              {healthPercent}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-800 overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-700 ease-out',
                status === 'healthy' && 'bg-status-healthy',
                status === 'critical' && 'bg-status-critical',
                status === 'warning' && 'bg-status-warning',
                status === 'unknown' && 'bg-surface-600',
              )}
              style={{ width: `${healthPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Metrics row */}
      <div className="grid grid-cols-3 gap-3">
        <MetricPill icon={<Server className="w-3.5 h-3.5" />} value={team.services.total} label="Services" />
        <MetricPill icon={<Bot className="w-3.5 h-3.5" />} value={team.agent_count} label="Agents" />
        <MetricPill icon={<Users className="w-3.5 h-3.5" />} value={team.member_count} label="Members" />
      </div>

      {/* Hover arrow */}
      <ArrowUpRight className="absolute top-5 right-5 w-4 h-4 text-surface-600 opacity-0 group-hover:opacity-100 group-hover:text-primary-400 transition-all duration-200 -translate-x-1 group-hover:translate-x-0" />
    </button>
  );
}

function MetricPill({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div className="flex items-center gap-2 bg-white/[0.03] rounded-lg px-2.5 py-1.5">
      <span className="text-surface-500">{icon}</span>
      <div className="min-w-0">
        <p className="text-white font-mono text-sm font-semibold leading-none">{value}</p>
        <p className="text-surface-500 text-[10px] mt-0.5 leading-none">{label}</p>
      </div>
    </div>
  );
}
